import { DatePipe, DecimalPipe } from '@angular/common';
import { etiquetaVaca, rpVaca } from '@shared/utils/etiqueta-vaca';
import { Badge } from '@shared/components/ui/badge/badge';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AppFormComponent } from '@shared/components/ui/list-components';
import { ErrorService } from '@shared/services/error.service';
import { InputFieldComponent } from '@shared/components/form/input/input-field.component';
import { LabelComponent } from '@shared/components/form/label/label.component';
import { SelectFieldComponent, SelectOption } from '@shared/components/form/select/select-field.component';
import { SnackbarService } from '@shared/services/snackbar.service';
import { Spinner } from '@shared/components/ui/spinner/spinner';
import { forkJoin } from 'rxjs';

import {
  AnimalResumen,
  OrigenLaboratorioDetalle,
  StockVitrificado,
  StockVitrificadoEmbrion,
  StockVitrificadoPayload,
  StockVitrificadoSalida,
} from '../embriones.interface';
import { EmbrionesService } from '../embriones.service';
import { Card } from '@shared/components/common/card/card';
import { PageHeaderComponent } from '@shared/components/common/page-header/page-header.component';
import { DialogService } from '@shared/services/dialog.service';
import { ModalConfirmationService } from '@shared/services/modal-confirmation.service';
import { EditarOpsPajuela } from '../editar-ops-pajuela/editar-ops-pajuela';
import { RegistrarSalidaStock } from '../registrar-salida-stock/registrar-salida-stock';
// import { TransferirPajuelas } from '../../../agendamientos/transferencias/transferir-pajuelas/transferir-pajuelas';
// import { AuthenticationService } from '@core/auth/authentication.service';

@Component({
  selector: 'app-detalle-stock-vitrificado',
  imports: [
    AppFormComponent,
    DatePipe,
    InputFieldComponent,
    LabelComponent,
    ReactiveFormsModule,
    SelectFieldComponent,
    Spinner,
    Card,
    PageHeaderComponent,
    Badge,
    DecimalPipe,
  ],
  templateUrl: './detalle-stock-vitrificado.html',
})
export class DetalleStockVitrificado implements OnInit {
  /** La vaca se identifica por su RP, como en la caravana. */
  readonly etiquetaVaca = etiquetaVaca;
  readonly rpVaca = rpVaca;

  stockVitrificado!: StockVitrificado;
  editForm!: FormGroup;
  isEditing = false;
  loading = false;
  loadingStock = true;
  loadingCombos = false;

  donantes: AnimalResumen[] = [];
  toros: AnimalResumen[] = [];
  // Issue #20 (2026-09-15): el raque es texto libre; el combo del paramétrico
  // ya no se carga.
  // raques: ParamResumen[] = [];

  get donantesOptions(): SelectOption[] {
    return this.donantes.map(donante => ({ value: donante.id, label: etiquetaVaca(donante, true) }));
  }

  get torosOptions(): SelectOption[] {
    return this.toros.map(toro => ({ value: toro.id, label: toro.hbp ? `${toro.nombre} (${toro.hbp})` : toro.nombre }));
  }

  // get raquesOptions(): SelectOption[] {
  //   return this.raques.map(raque => ({ value: raque.id, label: raque.nombre }));
  // }

  get isLaboratoryOrigin(): boolean {
    return this.stockVitrificado?.origen_laboratorio ?? false;
  }

  // ---------------------------------------------------------------------
  // Salidas de stock (donación, venta, pérdida)
  // ---------------------------------------------------------------------

  seleccionados = new Set<number>();
  anulandoSalidaId: number | null = null;

  get embriones(): StockVitrificadoEmbrion[] {
    return this.stockVitrificado?.embriones ?? [];
  }

  get embrionesDisponibles(): StockVitrificadoEmbrion[] {
    return this.embriones.filter(embrion => embrion.estado === 'disponible');
  }

  /**
   * La marca física de una pajuela ya consumida o reservada no se puede cambiar:
   * la pajuela está rotulada y viajando.
   */
  puedeEditarOps(embrion: StockVitrificadoEmbrion): boolean {
    return embrion.estado === 'disponible';
  }

  editarOpsPajuela(embrion: StockVitrificadoEmbrion, event: Event): void {
    event.stopPropagation();

    this.dialogService.openForm(EditarOpsPajuela, {
      size: 'md',
      data: {
        stockId: this.stockVitrificado.id,
        fechaVit: this.stockVitrificado.fecha_vit,
        pajuela: embrion,
      },
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.getStockVitrificado(this.stockVitrificado.id);
      }
    });
  }

  get salidas(): StockVitrificadoSalida[] {
    return this.stockVitrificado?.salidas ?? [];
  }

  get embrionesSeleccionados(): StockVitrificadoEmbrion[] {
    return this.embriones.filter(embrion => this.seleccionados.has(embrion.id));
  }

  get totalEmbrionesSeleccionados(): number {
    return this.embrionesSeleccionados.reduce((total, embrion) => total + embrion.nro_embriones, 0);
  }

  get todosDisponiblesSeleccionados(): boolean {
    return this.embrionesDisponibles.length > 0
      && this.embrionesDisponibles.every(embrion => this.seleccionados.has(embrion.id));
  }

  isSeleccionado(embrion: StockVitrificadoEmbrion): boolean {
    return this.seleccionados.has(embrion.id);
  }

  toggleSeleccion(embrion: StockVitrificadoEmbrion): void {
    if (embrion.estado !== 'disponible') {
      return;
    }

    if (this.seleccionados.has(embrion.id)) {
      this.seleccionados.delete(embrion.id);
      return;
    }

    this.seleccionados.add(embrion.id);
  }

  toggleSeleccionarTodos(): void {
    if (this.todosDisponiblesSeleccionados) {
      this.seleccionados.clear();
      return;
    }

    this.seleccionados = new Set(this.embrionesDisponibles.map(embrion => embrion.id));
  }

  // 2026-09-23: la transferencia se hace desde la tabla de stock (stock-embriones-congelados).
  // /** Reservar las pajuelas en una transferencia: las receptoras se cargan en su planilla. */
  // readonly puedeTransferir = inject(AuthenticationService).hasPermission('laboratorio', 'create');
  //
  // abrirTransferir(): void {
  //   if (this.embrionesSeleccionados.length === 0) {
  //     return;
  //   }
  //
  //   this.dialogService.openForm(TransferirPajuelas, {
  //     size: 'md',
  //     data: {
  //       clienteId: this.stockVitrificado.cli_cliente_id,
  //       pajuelaIds: this.embrionesSeleccionados.map(embrion => embrion.id),
  //     },
  //   });
  // }

  abrirRegistrarSalida(): void {
    if (this.embrionesSeleccionados.length === 0) {
      return;
    }

    this.dialogService.openForm(RegistrarSalidaStock, {
      size: 'md',
      data: {
        stockId: this.stockVitrificado.id,
        embriones: this.embrionesSeleccionados,
        fechaVit: this.stockVitrificado.fecha_vit,
      },
    }).afterClosed().subscribe((stockActualizado?: StockVitrificado) => {
      if (stockActualizado) {
        this.aplicarStockActualizado(stockActualizado);
      }
    });
  }

  anularSalida(salida: StockVitrificadoSalida): void {
    if (!salida.permite_anulacion_manual) {
      return;
    }

    this.modalConfirmationService.open({
      title: 'Anular salida de stock',
      message: `¿Confirma anular la salida por ${salida.motivo_label.toLowerCase()} de ${salida.nro_embriones} embrión(es)? El embrión vuelve al stock disponible.`,
      actions: { confirm: { label: 'Anular', color: 'danger' } },
    }).afterClosed().subscribe((result) => {
      if (result !== 'confirmed') {
        return;
      }

      this.anulandoSalidaId = salida.id;

      this.embrionesService.anularSalidaStock(this.stockVitrificado.id, salida.id).subscribe({
        next: (response) => {
          if (response.status && response.data) {
            this.snackbarService.showSnackBar('Salida anulada correctamente');
            this.aplicarStockActualizado(response.data);
          }
        },
        error: (error) => {
          this.errorService.handleError(error);
        },
      }).add(() => {
        this.anulandoSalidaId = null;
      });
    });
  }

  salidaBadgeColor(salida: StockVitrificadoSalida): 'success' | 'info' | 'warning' | 'error' {
    switch (salida.motivo) {
      case 'T': return 'success';
      case 'V': return 'info';
      case 'D': return 'warning';
      default: return 'error';
    }
  }

  private aplicarStockActualizado(stock: StockVitrificado): void {
    this.stockVitrificado = stock;
    this.seleccionados.clear();
  }

  get origenLaboratorioDetalle(): OrigenLaboratorioDetalle | null {
    return this.stockVitrificado?.origen_laboratorio_detalle ?? null;
  }

  destinoLabel(destino: string | null): string {
    switch (destino) {
      case 'V': return 'Congelado';
      case 'F': return 'Fresco';
      default: return '-';
    }
  }

  private fB = inject(FormBuilder);
  private embrionesService = inject(EmbrionesService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialogService = inject(DialogService);
  private modalConfirmationService = inject(ModalConfirmationService);

  ngOnInit(): void {
    this.getStockVitrificado(Number(this.route.snapshot.paramMap.get('id')));
  }

  private getStockVitrificado(id: number): void {
    this.loadingStock = true;

    this.embrionesService.getStockVitrificado(id).subscribe({
      next: (response) => {
        if (response.status && response.data) {
          this.stockVitrificado = response.data;
          this.makeForm();
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      },
    }).add(() => {
      this.loadingStock = false;
    });
  }

  private makeForm(): void {
    this.editForm = this.fB.group({
      cli_cliente_id: [this.stockVitrificado.cli_cliente_id, Validators.required],
      donante_id: [this.stockVitrificado.donante_id, Validators.required],
      toro_id: [this.stockVitrificado.toro_id, Validators.required],
      raque: [this.stockVitrificado.raque ?? ''],
      fecha_opu: [this.stockVitrificado.fecha_opu, Validators.required],
      fecha_vit: [this.stockVitrificado.fecha_vit, Validators.required],
      nro_embriones: [this.stockVitrificado.nro_embriones, [Validators.required, Validators.min(0)]],
      fecha_te: [this.stockVitrificado.fecha_te || ''],
      te: [this.stockVitrificado.te, [Validators.min(0)]],
      descarte: [this.stockVitrificado.descarte, [Validators.min(0)]],
    });
  }

  toggleEdit(): void {
    if (this.isLaboratoryOrigin) {
      return;
    }

    if (!this.isEditing) {
      this.loadCombos();
    }

    this.isEditing = !this.isEditing;
  }

  private loadCombos(): void {
    this.loadingCombos = true;
    const clienteId = this.stockVitrificado.cli_cliente_id;

    forkJoin([
      this.embrionesService.getDonantes(clienteId),
      this.embrionesService.getToros(),
    ]).subscribe({
      next: ([donantesResponse, torosResponse]) => {
        this.donantes = donantesResponse.data || [];
        this.toros = torosResponse.data || [];
      },
      error: (error) => {
        this.errorService.handleError(error);
      },
    }).add(() => {
      this.loadingCombos = false;
    });
  }

  cancelEdit(): void {
    this.isEditing = false;
    this.makeForm();
  }

  save(): void {
    if (this.editForm.invalid) {
      return;
    }

    this.loading = true;
    this.embrionesService.updateStockVitrificado(this.stockVitrificado.id, this.editForm.value as StockVitrificadoPayload).subscribe({
      next: (response) => {
        if (response.status && response.data) {
          this.snackbarService.showSnackBar('Stock vitrificado actualizado correctamente');
          this.stockVitrificado = response.data;
          this.isEditing = false;
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      },
    }).add(() => {
      this.loading = false;
    });
  }

  close(): void {
    this.router.navigate(['/stock'], { queryParams: { tab: 'embriones' } });
  }

  goToAgendamientoOpu(): void {
    const agendamientoId = this.origenLaboratorioDetalle?.lab_agendamiento_opu_id;

    if (!agendamientoId) {
      return;
    }

    this.router.navigate(['/pive', agendamientoId]);
  }
}
